import { Fragment } from 'react'
import Flex from '@/elements/Flex'
import FormToggle from '@/elements/FormToggle'
import Text from '@/elements/Text'
import useNotifications from '@/hooks/useNotifications'
import { NotificationChannels, type NotificationSettingResponse } from '@/api/types/Notification'
import Loading from '@/elements/Loading'
import { formatAsTitle } from '@/utils/format'
import useBreakpoints from '@/hooks/useBreakpoints'

interface NotificationsProps {
  loading: boolean
  notifications: NotificationSettingResponse[]
  onSave: (id: NotificationSettingResponse['id'], value: NotificationSettingResponse['value']) => void
}

function NotificationSettings({ loading, notifications, onSave }: NotificationsProps) {
  const { isMobile } = useBreakpoints()
  const {
    notificationGrouping,
    getNotificationTypeName,
    getNotificationTypeDescription
  } = useNotifications()

  if (loading) {
    return <Flex wfull style={{ height: 200 }}><Loading /></Flex>
  }

  const notificationsForChannel = (notificationsForType: NotificationSettingResponse[], channel: string) => {
    return notificationsForType.filter(n => n.channel === channel)
  }

  return (
    <Flex gap={!isMobile ? 5 : 2}>
      {Object.entries(notificationGrouping).map(([groupName, types], groupIndex) => {
        const settingsToShow = types.map(type => {
          return notifications.filter(n => n.code === type && n.enabled)
        }).filter(group => group && group.length > 0) as NotificationSettingResponse[][]

        if (settingsToShow.length === 0) return null

        return (
          <Fragment key={groupName} >
            <Flex
              wfull
              gap={3}
              p={isMobile ? 3 : 'unset'}
              pb={isMobile ? 4 : 'unset'}
              color={isMobile ? 'gray-light-60' : undefined}
              radius={isMobile ? 'medium' : undefined}
            >
              <Text variant='callout' color='black-light-40'>
                {groupName}
              </Text>
              <Flex wfull gap={!isMobile ? 3 : 2} >
                {settingsToShow.map((notificationsForType, notificationsIndex) => (
                  <Flex
                    key={notificationsForType[0].id}
                    wfull
                    inline={!isMobile}
                    gap={2}
                    align={!isMobile ? 'center' : 'flex-start'}
                    justify='space-between'
                  >
                    <Flex wfull mr={!isMobile ? 2 : 'unset'} pb={isMobile ? 2 : 'unset'}>
                      <Text variant='h4' color='black'>
                        {getNotificationTypeName(notificationsForType[0].code)}
                      </Text>
                      <Text variant='textblock' color='black-light-40'>
                        {getNotificationTypeDescription(notificationsForType[0].code)}
                      </Text>
                    </Flex>
                    <Flex inline gap={isMobile ? 6 : 2} ml={2}>
                      {NotificationChannels.map(channel => (
                        <Flex
                          key={channel}
                          align='center'
                          gap={1}
                          inline={isMobile}
                          style={{ minWidth: 54 }}
                        >
                          <Text variant='footnote' color='black-light-60'>
                            {formatAsTitle(channel)}
                          </Text>
                          {notificationsForChannel(notificationsForType, channel).length > 0 ? (
                            notificationsForChannel(notificationsForType, channel).map(notification => (
                              <FormToggle
                                key={notification.id}
                                name={`${notification.code}_${notification.channel}`}
                                value={notification.value}
                                onChange={event => onSave(notification.id, event.target.checked)}
                                disabled={!notification.enabled}
                              />
                            ))
                          ) : (
                            <Flex justify='center' style={{ minHeight: 28 }}>
                              <Text variant='footnote' color='black-light-40'>N/A</Text>
                            </Flex>
                          )}
                        </Flex>
                      ))}
                    </Flex>
                    {isMobile && notificationsIndex < settingsToShow.length - 1 && (
                      <Flex color='gray' wfull my={3} style={{ height: '1px' }} />
                    )}
                  </Flex>
                ))}
              </Flex>
            </Flex>
            {!isMobile && groupIndex < Object.entries(notificationGrouping).length - 1 && (
              <Flex color='gray-light-20' wfull style={{ height: '1px' }} />
            )}
          </Fragment>
        )
      })}
    </Flex>
  )
}

export default NotificationSettings
